/**
 * Canned prompts for the Product Management AI assistant.
 *
 * Picking one pastes its text into the prompt box for the operator to edit before sending — it is
 * never sent as-is. That's the point of the bracketed placeholders: they don't get substituted by
 * anything, they're there to make the operator fill in the specifics themselves, which is both
 * faster than composing from scratch and a nudge to actually read what they're about to ask for.
 *
 * `preset_key` is recorded against every request in the activity log, so "which of these do people
 * actually use" is answerable later and this list can be trimmed on evidence rather than taste.
 *
 * The REVIEW group is read-only by design — those prompts ask questions and the assistant answers
 * in prose without proposing any change. They're the safest way for someone to get familiar with
 * the assistant before letting it touch anything.
 */

export const PRESET_GROUPS = [
  { key: 'pricing', label: 'Pricing' },
  { key: 'products', label: 'Add, copy & rename' },
  { key: 'availability', label: 'Availability' },
  { key: 'config', label: 'Product settings' },
  { key: 'review', label: 'Review & audit (read-only)' },
];

export const PRESET_PROMPTS = [
  // ---------------------------------------------------------------------------- Pricing
  {
    key: 'price_all_up_percent',
    group: 'pricing',
    label: 'Increase every price by a percentage',
    prompt: 'Increase all dine-in and takeaway prices across the whole menu by 5%.',
  },
  {
    key: 'price_all_down_percent',
    group: 'pricing',
    label: 'Reduce every price by a percentage',
    prompt: 'Reduce all dine-in and takeaway prices across the whole menu by 5%.',
  },
  {
    key: 'price_category_percent',
    group: 'pricing',
    label: 'Change prices for one category',
    prompt: 'Increase all prices in the [CATEGORY NAME] category by 10%.',
  },
  {
    key: 'price_subcategory_percent',
    group: 'pricing',
    label: 'Change prices for one sub-category',
    prompt: 'Increase all prices in the [SUB-CATEGORY NAME] sub-category by 10%.',
  },
  {
    key: 'price_all_except_category',
    group: 'pricing',
    label: 'Change all prices except one category',
    prompt: 'Increase all prices by 5%, but leave the [CATEGORY NAME] category unchanged.',
  },
  {
    key: 'price_flat_amount',
    group: 'pricing',
    label: 'Add a flat dollar amount to prices',
    prompt: 'Add $1.00 to the dine-in price of every product in the [CATEGORY NAME] category.',
  },
  {
    key: 'price_round_50c',
    group: 'pricing',
    label: 'Round prices to the nearest 50c',
    prompt: 'Round every dine-in and takeaway price on the menu to the nearest $0.50.',
  },
  {
    key: 'price_round_5c',
    group: 'pricing',
    label: 'Round prices to the nearest 5c',
    prompt: 'Round every dine-in and takeaway price on the menu to the nearest $0.05 so there are no awkward cents.',
  },
  {
    key: 'price_takeaway_surcharge',
    group: 'pricing',
    label: 'Apply a takeaway surcharge',
    prompt: 'Set takeaway prices to $1.00 more than the dine-in price for every product in the [CATEGORY NAME] category.',
  },
  {
    key: 'price_match_takeaway_to_dinein',
    group: 'pricing',
    label: 'Match takeaway prices to dine-in',
    prompt: 'Make the takeaway price the same as the dine-in price for every product on the menu.',
  },
  {
    key: 'price_match_dinein_to_takeaway',
    group: 'pricing',
    label: 'Match dine-in prices to takeaway',
    prompt: 'Make the dine-in price the same as the takeaway price for every product on the menu.',
  },
  {
    key: 'price_single_product',
    group: 'pricing',
    label: "Set one product's price",
    prompt: 'Set the price of [PRODUCT NAME] to $[PRICE] for both dine-in and takeaway, for every size it has.',
  },
  {
    key: 'price_single_product_one_size',
    group: 'pricing',
    label: "Set one size's price on one product",
    prompt: 'Set the [SIZE NAME] price of [PRODUCT NAME] to $[PRICE] for both dine-in and takeaway.',
  },
  {
    key: 'price_category_flat',
    group: 'pricing',
    label: 'Set a whole category to one flat price',
    prompt: 'Set every product in the [CATEGORY NAME] category to $[PRICE] for both dine-in and takeaway, across all of their sizes.',
  },
  {
    key: 'price_one_size_only',
    group: 'pricing',
    label: 'Re-price one size only',
    prompt: 'Increase only the [SIZE NAME] price by 10% for every product in the [CATEGORY NAME] category. Leave the other sizes alone.',
  },
  {
    key: 'price_dine_in_only',
    group: 'pricing',
    label: 'Change dine-in prices only',
    prompt: 'Increase dine-in prices by 8% across the whole menu. Do not change any takeaway or website prices.',
  },
  {
    key: 'price_all_three_channels',
    group: 'pricing',
    label: 'Change website, dine-in and takeaway together',
    prompt: 'Increase the website, dine-in and takeaway prices by 5% across the whole menu.',
  },
  {
    key: 'price_website_only',
    group: 'pricing',
    label: 'Change website prices only',
    prompt: 'Increase only the website prices in the [CATEGORY NAME] category by 10%. Leave the dine-in and takeaway prices alone.',
  },
  {
    key: 'price_copy_website_to_pos',
    group: 'pricing',
    label: 'Copy website prices onto the POS',
    prompt: 'Set the dine-in and takeaway prices to match the website price for every product on the menu.',
  },

  // ---------------------------------------------------------------------------- Add, copy, rename
  {
    key: 'product_add',
    group: 'products',
    label: 'Add a new product',
    prompt: 'Add a new product called [PRODUCT NAME] to the [CATEGORY NAME] category, priced at $[PRICE] for dine-in and takeaway.',
  },
  {
    key: 'product_add_several',
    group: 'products',
    label: 'Add several products at once',
    prompt: 'Add these products to the [CATEGORY NAME] category: [NAME 1] at $[PRICE], [NAME 2] at $[PRICE], [NAME 3] at $[PRICE].',
  },
  {
    key: 'product_duplicate',
    group: 'products',
    label: 'Duplicate a product at a new price',
    prompt: 'Duplicate [PRODUCT NAME] and call the copy [NEW NAME]. Keep all of its sizes and set each size\'s price to $[PRICE].',
  },
  {
    key: 'product_duplicate_premium',
    group: 'products',
    label: 'Duplicate as a premium/larger version',
    prompt: 'Duplicate [PRODUCT NAME] as "[PRODUCT NAME] - Large", keeping all its sizes, and add $[AMOUNT] to each size\'s price.',
  },
  {
    key: 'product_rename',
    group: 'products',
    label: 'Rename a product',
    prompt: 'Rename [CURRENT PRODUCT NAME] to [NEW NAME].',
  },
  {
    key: 'product_short_name',
    group: 'products',
    label: 'Set a short name for receipts',
    prompt: 'Set the short name of [PRODUCT NAME] to [SHORT NAME] so it fits on kitchen dockets.',
  },
  {
    key: 'product_move_category',
    group: 'products',
    label: 'Move a product to another category',
    prompt: 'Move [PRODUCT NAME] into the [CATEGORY NAME] category.',
  },
  {
    key: 'product_move_subcategory',
    group: 'products',
    label: 'Move a product to another sub-category',
    prompt: 'Move [PRODUCT NAME] into the [SUB-CATEGORY NAME] sub-category.',
  },
  {
    key: 'product_tidy_names',
    group: 'products',
    label: 'Tidy up product naming',
    prompt: 'The products in the [CATEGORY NAME] category have inconsistent capitalisation. Rename them to use Title Case. List what you would change.',
  },

  // ---------------------------------------------------------------------------- Availability
  {
    key: 'avail_disable_product',
    group: 'availability',
    label: 'Mark a product out of stock',
    prompt: 'Disable [PRODUCT NAME] — we have run out for today.',
  },
  {
    key: 'avail_enable_product',
    group: 'availability',
    label: 'Put a product back on sale',
    prompt: 'Re-enable [PRODUCT NAME] — it is back in stock.',
  },
  {
    key: 'avail_disable_several',
    group: 'availability',
    label: 'Mark several products out of stock',
    prompt: 'Disable these products for today: [NAME 1], [NAME 2], [NAME 3].',
  },
  {
    key: 'avail_disable_category',
    group: 'availability',
    label: 'Disable a whole category',
    prompt: 'Disable the [CATEGORY NAME] category so it does not show on the ordering screen.',
  },
  {
    key: 'avail_enable_category',
    group: 'availability',
    label: 'Enable a whole category',
    prompt: 'Enable the [CATEGORY NAME] category.',
  },
  {
    key: 'avail_disable_subcategory',
    group: 'availability',
    label: 'Disable a sub-category',
    prompt: 'Disable the [SUB-CATEGORY NAME] sub-category.',
  },
  {
    key: 'avail_disable_all_in_subcategory',
    group: 'availability',
    label: 'Disable every product in a sub-category',
    prompt: 'Disable every product in the [SUB-CATEGORY NAME] sub-category, but leave the sub-category itself enabled.',
  },
  {
    key: 'avail_seasonal_off',
    group: 'availability',
    label: 'Take seasonal items off the menu',
    prompt: 'Disable these seasonal items until further notice: [NAME 1], [NAME 2].',
  },

  // ---------------------------------------------------------------------------- Product settings
  {
    key: 'config_popup_on_category',
    group: 'config',
    label: 'Turn on the customisation pop-up for a category',
    prompt: 'Turn on the customisation pop-up for every product in the [CATEGORY NAME] category, so staff are always asked for options.',
  },
  {
    key: 'config_popup_off_category',
    group: 'config',
    label: 'Turn off the customisation pop-up for a category',
    prompt: 'Turn off the customisation pop-up for every product in the [CATEGORY NAME] category.',
  },
  {
    key: 'config_popup_on_product',
    group: 'config',
    label: 'Turn on the customisation pop-up for one product',
    prompt: 'Turn on the customisation pop-up for [PRODUCT NAME].',
  },
  {
    key: 'config_spicy_on',
    group: 'config',
    label: 'Mark products as spicy',
    prompt: 'Mark these products as spicy: [NAME 1], [NAME 2], [NAME 3].',
  },
  {
    key: 'config_spicy_off',
    group: 'config',
    label: 'Remove the spicy marking',
    prompt: 'Remove the spicy marking from [PRODUCT NAME].',
  },

  // ---------------------------------------------------------------------------- Review (read-only)
  {
    key: 'review_no_price',
    group: 'review',
    label: 'Which products have no price set?',
    prompt: 'Which products have no price set, or a price of $0? Just list them — do not change anything.',
  },
  {
    key: 'review_duplicates',
    group: 'review',
    label: 'Are any products duplicated?',
    prompt: 'Are there any products that look like duplicates of each other, by name or by price? Just list them — do not change anything.',
  },
  {
    key: 'review_disabled',
    group: 'review',
    label: 'What is currently disabled?',
    prompt: 'List every product, category and sub-category that is currently disabled. Do not change anything.',
  },
  {
    key: 'review_no_subcategory',
    group: 'review',
    label: 'Which products have no sub-category?',
    prompt: 'Which products have no sub-category assigned? Just list them grouped by category — do not change anything.',
  },
  {
    key: 'review_channel_mismatch',
    group: 'review',
    label: 'Where do dine-in and takeaway prices differ?',
    prompt: 'Which products have a takeaway price that differs from their dine-in price, and by how much? Just report — do not change anything.',
  },
  {
    key: 'review_zero_channel_prices',
    group: 'review',
    label: 'Which products rely on the website price fallback?',
    prompt: 'Which products have a dine-in or takeaway price of 0 and are therefore falling back to their website price? Group them by category and just report — do not change anything.',
  },
  {
    key: 'review_odd_cents',
    group: 'review',
    label: 'Which prices have awkward cents?',
    prompt: 'Which prices do not end in .00, .50, .95 or .99? Just list them — do not change anything.',
  },
  {
    key: 'review_category_summary',
    group: 'review',
    label: 'Summarise the menu by category',
    prompt: 'Summarise the menu: for each category, how many products it has and the price range. Do not change anything.',
  },
  {
    key: 'review_extremes',
    group: 'review',
    label: 'Cheapest and most expensive items',
    prompt: 'What are the five cheapest and five most expensive items on the menu? Do not change anything.',
  },
  {
    key: 'review_missing_short_name',
    group: 'review',
    label: 'Which products have no short name?',
    prompt: 'Which products have a long name but no short name set for kitchen dockets? Just list them — do not change anything.',
  },
];

export const presetsByGroup = () =>
  PRESET_GROUPS.map((group) => ({
    ...group,
    presets: PRESET_PROMPTS.filter((preset) => preset.group === group.key),
  })).filter((group) => group.presets.length > 0);

export const findPreset = (key) => PRESET_PROMPTS.find((preset) => preset.key === key) || null;

/**
 * The bracketed slots a preset can contain, and how the UI should let someone fill each one.
 *
 * `source` names a list the assistant already loads for this restaurant, so a category, product or
 * size is picked from what actually exists rather than typed from memory - a typo'd name is the
 * single most common reason a request comes back with "nothing matched". Anything with no source
 * falls back to a plain input, and any bracketed token not listed here still gets a text input, so
 * adding a new preset never needs a change in this map.
 */
export const PLACEHOLDER_FIELDS = {
  '[CATEGORY NAME]':         { label: 'Category',        source: 'categories' },
  '[SUB-CATEGORY NAME]':     { label: 'Sub-category',    source: 'subcategories' },
  '[PRODUCT NAME]':          { label: 'Product',         source: 'products' },
  '[CURRENT PRODUCT NAME]':  { label: 'Product',         source: 'products' },
  '[SIZE NAME]':             { label: 'Size',            source: 'sizes' },
  '[PRICE]':                 { label: 'Price',           type: 'number', step: '0.01', min: '0', prefix: '$' },
  '[AMOUNT]':                { label: 'Amount',          type: 'number', step: '0.01', min: '0', prefix: '$' },
  '[NEW NAME]':              { label: 'New name' },
  '[SHORT NAME]':            { label: 'Short name' },
  '[NAME 1]':                { label: 'Name 1' },
  '[NAME 2]':                { label: 'Name 2' },
  '[NAME 3]':                { label: 'Name 3' },
};

/** Every bracketed slot in a prompt, in the order it appears, without duplicates. */
export const extractPlaceholders = (text) => {
  const found = String(text || '').match(/\[[^\]]+\]/g) || [];
  return [...new Set(found)];
};

/** Describes one slot, falling back to a text input for tokens the map doesn't know. */
export const describePlaceholder = (token) =>
  PLACEHOLDER_FIELDS[token] || { label: token.replace(/^\[|\]$/g, '').toLowerCase() };
